/* Phase complete bars (designs/products-pdp.md PP6 + UI "Phase area (2)"):
   one `.pct-row` per reached phase — the caller passes phaseBars()'s output,
   which already stops at the declared phase, so nothing here invents a bar.
   Solid = evidence, hatched = estimates; the red count sits beside the
   percent on purpose (62 % built on estimates and 62 % built on evidence
   must never look the same). Pure: plain props, no store. */
import type { JSX } from "react";
import type { PdpPhase } from "../../../../shared/pdp";
import type { PhaseBar, SlotCounts } from "./model";

const round1 = (n: number): number => Math.round(n * 10) / 10;

/* PP6 read back as two widths: every evidenced slot weighs 100, so the solid
   share is evidenced / owed; the rest of the percent is estimates */
export const barShares = (bar: PhaseBar): { solid: number; hatch: number } => {
  const owed = bar.counts.evidenced + bar.counts.estimated + bar.counts.missing + bar.counts.unconfirmed;
  if (bar.percent === null || owed === 0) return { solid: 0, hatch: 0 };
  const solid = round1((bar.counts.evidenced / owed) * 100);
  return { solid, hatch: round1(Math.max(0, bar.percent - solid)) };
};

export const countsLine = (c: SlotCounts): string =>
  `${c.evidenced} evidenced · ${c.estimated} estimated · ${c.missing} not recorded` +
  (c.unconfirmed ? ` · ${c.unconfirmed} unconfirmed` : "");

const srLabel = (bar: PhaseBar): string =>
  bar.percent === null ? `Phase ${bar.phase}: nothing owed` : `Phase ${bar.phase}: ${bar.percent}% — ${countsLine(bar.counts)}`;

function Counts({ counts }: { counts: SlotCounts }): JSX.Element {
  return (
    <span className="pct-counts">
      {counts.evidenced} evidenced · {counts.estimated} estimated ·{" "}
      <span className="state missing">{counts.missing} not recorded</span>
      {counts.unconfirmed ? (
        <>
          {" · "}
          <span className="state unconfirmed">{counts.unconfirmed} unconfirmed</span>
        </>
      ) : null}
    </span>
  );
}

function Row({ bar, onOpenPhase }: { bar: PhaseBar; onOpenPhase?: (phase: PdpPhase) => void }): JSX.Element {
  const { solid, hatch } = barShares(bar);
  const body = (
    <>
      <span className="pct-label">Phase {bar.phase}</span>
      <div className="pct-track" role="img" aria-label={srLabel(bar)}>
        <span className="pct-solid" style={{ width: `${solid}%` }} />
        <span className="pct-hatch" style={{ width: `${hatch}%` }} />
      </div>
      <span className="pct-num">{bar.percent === null ? "nothing owed" : `${bar.percent}%`}</span>
      {bar.percent === null ? null : <Counts counts={bar.counts} />}
    </>
  );
  return (
    <li className="pct-row">
      {onOpenPhase ? (
        <button type="button" className="pct-open" onClick={() => onOpenPhase(bar.phase)}>
          {body}
        </button>
      ) : (
        <div className="pct-open">{body}</div>
      )}
    </li>
  );
}

export function PhaseBars({
  bars,
  onOpenPhase,
}: {
  bars: PhaseBar[];
  /* a row is a button that opens Deliverables filtered to its phase; absent
     (the review sheet) the row is plain */
  onOpenPhase?: (phase: PdpPhase) => void;
}): JSX.Element | null {
  if (bars.length === 0) return null;
  return (
    <ul className="pct-bars">
      {bars.map((bar) => (
        <Row key={bar.phase} bar={bar} onOpenPhase={onOpenPhase} />
      ))}
    </ul>
  );
}
